import { DurationFormat } from "@formatjs/intl-durationformat"
import {
  DateFormatter,
  getLocalTimeZone,
  now,
  parseAbsoluteToLocal,
  today,
} from "@internationalized/date"
import { TFunction } from "i18next"
import { Pass, RedemptionConfig } from "ipctg-inflow-types/InFlowDB"

import {
  formatTimeOfDay,
  getLocale,
  getTranslatedDayName,
  toDuration,
} from "./etc"

/**
 * Checks if a pass is expired based on its validTo date
 */
export function isPassExpired(pass: Pass): boolean {
  return (
    parseAbsoluteToLocal(pass.validTo).compare(now(getLocalTimeZone())) <= 0
  )
}

/**
 * Checks if a pass has been fully redeemed (all uses consumed)
 */
export function isPassFullyRedeemed(pass: Pass): boolean {
  return pass.uses >= pass.maxUses
}

export function getPassSessionLengthCopy(args: {
  redemptionConfig: RedemptionConfig | undefined
  locale: string | undefined
  t: TFunction<"passes", undefined>

  /**
   * purchase: pre-sale card, session start unknown.
   * redeem: a session starts now — copy may state concrete dates.
   * details: describes an owned-but-unredeemed pass (account page); same as
   *   redeem except copy must stay start-relative, since "now" isn't the start.
   */
  mode: "purchase" | "redeem" | "details"
  timeZone?: string
}) {
  if (!args.redemptionConfig) {
    if (args.mode === "redeem") {
      return args.t("purchasedProducts.quotaPack.redeem.startSession")
    }
    return ""
  }

  switch (args.redemptionConfig.type) {
    case "ValidFor": {
      // CalendarDay is a deadline, not a duration: the session ends at
      // 11:59pm (location time), intervalCount calendar days after it starts.
      if (args.redemptionConfig.intervalPeriod === "CalendarDay") {
        const endTime = formatTimeOfDay("23:59", args.locale)
        const count = args.redemptionConfig.intervalCount

        // At purchase time the session start is unknown (multi-use pass), so
        // the copy stays relative to the eventual start.
        if (args.mode === "purchase") {
          return args.t(
            "purchase.index.card.specifications.quotaPack.sessionCalendarDays",
            { count, endTime }
          )
        }

        if (args.mode === "details") {
          return args.t(
            "purchasedProducts.quotaPack.redeem.parkCalendarDaysRelative",
            { count, endTime }
          )
        }

        const timeZone = args.timeZone || getLocalTimeZone()
        const startDay = today(timeZone)
        const endDay = startDay.add({ days: count })
        const endDate = new DateFormatter(args.locale || getLocale(), {
          year: endDay.year === startDay.year ? undefined : "numeric",
          month: "short",
          day: "numeric",
          timeZone,
        }).format(endDay.toDate(timeZone))

        return args.t("purchasedProducts.quotaPack.redeem.parkCalendarDays", {
          endTime,
          endDate,
        })
      }

      const durationString = new DurationFormat(args.locale || getLocale(), {
        style: "long",
      }).format(toDuration(args.redemptionConfig))

      if (args.mode === "purchase") {
        return args.t(
          "purchase.index.card.specifications.quotaPack.sessionLength",
          {
            sessionLength: durationString,
          }
        )
      } else {
        return args.t("purchasedProducts.quotaPack.redeem.parkFor", {
          sessionLength: durationString,
        })
      }
    }
    case "ValidUntilExpiration": {
      const expirationTime = formatTimeOfDay(
        args.redemptionConfig.expirationTime,
        args.locale
      )

      if (args.mode === "purchase") {
        return args.t(
          "purchase.index.card.specifications.quotaPack.sessionUntil",
          { expirationTime }
        )
      } else {
        return args.t("purchasedProducts.quotaPack.redeem.parkUntil", {
          expirationTime,
        })
      }
    }
    case "ValidUntilDeparture": {
      const departureTime = formatTimeOfDay(
        args.redemptionConfig.departureTime,
        args.locale
      )
      const untilDay = args.redemptionConfig.untilDay
        ? getTranslatedDayName(args.redemptionConfig.untilDay, args.locale)
        : null
      const nights = args.redemptionConfig.nights

      if (args.mode === "purchase") {
        if (untilDay) {
          return args.t(
            "purchase.index.card.specifications.quotaPack.sessionUntilDepartureWithDay",
            { departureTime, untilDay }
          )
        }
        if (nights) {
          return args.t(
            "purchase.index.card.specifications.quotaPack.sessionUntilDepartureNights",
            { count: nights, departureTime }
          )
        }
        return args.t(
          "purchase.index.card.specifications.quotaPack.sessionUntilDeparture",
          { departureTime }
        )
      } else {
        if (untilDay) {
          return args.t(
            "purchasedProducts.quotaPack.redeem.parkUntilDepartureWithDay",
            { departureTime, untilDay }
          )
        }
        if (nights) {
          return args.t(
            "purchasedProducts.quotaPack.redeem.parkUntilDepartureNights",
            { count: nights, departureTime }
          )
        }
        return args.t("purchasedProducts.quotaPack.redeem.parkUntilDeparture", {
          departureTime,
        })
      }
    }
    default: {
      // If this happens, the configuration is incorrect or we haven't updated to handle the new cases
      console.error("Unsupported redemption type: ", args.redemptionConfig.type)
      if (args.mode === "redeem") {
        return args.t("purchasedProducts.quotaPack.redeem.startSession")
      }
      return ""
    }
  }
}
